import { __awaiter } from "tslib";
import { makeAutoObservable, runInAction, when } from "mobx";
import { applySnapshot, unprotect } from "@concord-consortium/mobx-state-tree";
import { typeConverter } from "../../utilities/db-utils";
import { MetadataDocMapModel } from "../document/document-metadata-model";
import { isSortableType } from "../document/document-types";
import { DocumentGroup } from "./document-group";
export class SortedDocuments {
    constructor(stores) {
        Object.defineProperty(this, "stores", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "metadataDocsFiltered", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: MetadataDocMapModel.create()
        });
        // `metadataDocsWithoutUnit` picks up unit-less docs (e.g. personal documents) when a unit
        // filter is applied.
        Object.defineProperty(this, "metadataDocsWithoutUnit", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: MetadataDocMapModel.create()
        });
        Object.defineProperty(this, "docsReceived", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        // Maps from document ID to the history entry ID that the user requested to view.
        Object.defineProperty(this, "documentHistoryViewRequests", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {}
        });
        // Root document group that serves as the root node for delegating all sorting operations.
        Object.defineProperty(this, "rootDocumentGroup", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        makeAutoObservable(this);
        this.stores = stores;
        // We only want MobX observability + MST serialization, not MST actions, on these maps.
        unprotect(this.metadataDocsFiltered);
        unprotect(this.metadataDocsWithoutUnit);
        this.rootDocumentGroup = new DocumentGroup({
            stores,
            sortType: "All",
            label: "All Documents",
            documents: () => this.filteredDocsByType
        });
    }
    get bookmarksStore() {
        return this.stores.bookmarks;
    }
    get class() {
        return this.stores.class;
    }
    get commentTags() {
        return this.stores.commentTags.mergedWith(this.stores.appConfig.commentTags);
    }
    get db() {
        return this.stores.db;
    }
    get documents() {
        return this.stores.documents;
    }
    get filteredDocsByType() {
        return this.firestoreMetadataDocs.filter((doc) => {
            return isSortableType(doc.type);
        });
    }
    get groupsStore() {
        return this.stores.groups;
    }
    get user() {
        return this.stores.user;
    }
    get curriculumConfig() {
        return this.stores.curriculumConfig;
    }
    setDocumentHistoryViewRequest(docKey, historyId) {
        this.documentHistoryViewRequests[docKey] = historyId;
    }
    getDocumentHistoryViewRequest(docKey) {
        // We only want to move to this history entry once,
        // so we delete the request after it has been fulfilled.
        const historyId = this.documentHistoryViewRequests[docKey];
        if (historyId) {
            delete this.documentHistoryViewRequests[docKey];
        }
        return historyId;
    }
    sortBy(sortType) {
        return this.rootDocumentGroup.sortBy(sortType);
    }
    watchFirestoreMetaDataDocs(filter, unit, investigation, problem) {
        const db = this.db.firestore;
        const converter = typeConverter();
        const baseQuery = db.collection("documents")
            .withConverter(converter)
            .where("context_id", "==", this.user.classHash);
        let filteredQuery = baseQuery;
        if (filter !== "All") {
            // an "in" query is used here so that we can find any documents that use unit and
            // any the older renamed unit codes.
            filteredQuery = filteredQuery.where("unit", "in", this.curriculumConfig.getUnitCodeVariants(unit));
        }
        if (filter === "Investigation" || filter === "Problem") {
            filteredQuery = filteredQuery.where("investigation", "==", String(investigation));
        }
        if (filter === "Problem") {
            filteredQuery = filteredQuery.where("problem", "==", String(problem));
        }
        const disposeFilteredListener = filteredQuery.onSnapshot(snapshot => {
            const mstSnapshot = this.stores.documentMetadata.getMSTSnapshotFromFBSnapshot(snapshot);
            runInAction(() => {
                applySnapshot(this.metadataDocsFiltered, mstSnapshot);
                this.docsReceived = true;
            });
        });
        let disposeDocsWithoutUnitListener;
        if (filter !== "All") {
            // We need to look for the unit-less documents like personal documents
            const queryForUnitNull = baseQuery.where("unit", "==", null);
            disposeDocsWithoutUnitListener = queryForUnitNull.onSnapshot(snapshot => {
                const mstSnapshot = this.stores.documentMetadata.getMSTSnapshotFromFBSnapshot(snapshot);
                applySnapshot(this.metadataDocsWithoutUnit, mstSnapshot);
            });
        }
        else {
            // If the filter is "All" then the metaDocsFiltered will include everything.
            this.metadataDocsWithoutUnit.clear();
        }
        // A disposing function that calls the two disposers from the
        // onSnapshot listeners.
        return () => {
            disposeFilteredListener();
            disposeDocsWithoutUnitListener === null || disposeDocsWithoutUnitListener === void 0 ? void 0 : disposeDocsWithoutUnitListener();
        };
    }
    get exemplarMetadataDocs() {
        return this.stores.documentMetadata.exemplarMetadataDocs;
    }
    // What happens if the visibility changes on a metadata document?
    // - FS onSnapshot listener is called
    // - listener applies the snapshot to the map of objects
    // - the keys of the objects don't change in the map
    // - MobX should not re-run this view because it is only reading the key
    //   of each document.
    get firestoreMetadataDocs() {
        const matchedDocKeys = new Set();
        const docsArray = [];
        this.metadataDocsFiltered.forEach(doc => {
            docsArray.push(doc);
            matchedDocKeys.add(doc.key);
        });
        this.metadataDocsWithoutUnit.forEach(doc => {
            // If there is a duplicate for some reason just ignore the unit-less one
            if (matchedDocKeys.has(doc.key))
                return;
            docsArray.push(doc);
            matchedDocKeys.add(doc.key);
        });
        this.exemplarMetadataDocs.forEach(doc => {
            // If there is a duplicate, it will have been merged with one of the previous
            // maps by the firestore snapshot listeners. So we ignore the duplicate here.
            if (matchedDocKeys.has(doc.key))
                return;
            docsArray.push(doc);
            matchedDocKeys.add(doc.key);
        });
        return docsArray;
    }
    // This only fetches documents that have metadata that was already pulled down
    // by the watchFirestoreMetaDataDocs listener.
    fetchFullDocument(docKey) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.docsReceived) {
                // Wait until the initial batch of documents has been received from Firestore.
                yield when(() => this.docsReceived);
            }
            const metadataDoc = this.firestoreMetadataDocs.find(doc => doc.key === docKey);
            if (!metadataDoc) {
                console.warn("Could not find metadata doc with key", docKey, this.firestoreMetadataDocs);
                return;
            }
            return this.db.openDocumentFromFirestoreMetadata(Object.assign(Object.assign({}, metadataDoc), { properties: metadataDoc.propertiesAsStringRecord }));
        });
    }
}
//# sourceMappingURL=data:application/json;base64,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